import React, { Fragment, useEffect, useState } from 'react'
import { Outlet, useNavigate, Link } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline'
import { Sidebar, SidebarSection } from './Sidebar'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/contexts/ThemeContext'
import { Avatar } from '@/components/common/Avatar'
import { NotificationDropdown } from '@/components/features/notifications/NotificationDropdown'

const instructorSections: SidebarSection[] = [
    {
        title: 'Dashboard',
        items: [
            { to: '/instructor', icon: 'dashboard', label: 'Overview' },
        ],
    },
    {
        title: 'Content',
        items: [
            { to: '/instructor/courses', icon: 'school', label: 'My Courses' },
            { to: '/instructor/modules', icon: 'folder', label: 'My Modules' },
        ],
    },
    {
        title: 'Finance',
        items: [
            { to: '/instructor/earnings', icon: 'attach_money', label: 'Earnings' },
        ],
    },
    {
        title: 'Schedule',
        items: [
            { to: '/instructor/batches', icon: 'calendar_month', label: 'My Batches' },
        ],
    },
    {
        title: 'Assessment',
        items: [
            { to: '/instructor/grading', icon: 'grading', label: 'Grading' },
            { to: '/instructor/quizzes', icon: 'quiz', label: 'Quiz Reviews' },
            { to: '/instructor/assignments', icon: 'assignment', label: 'Assignment Reviews' },
        ],
    },
]

export const InstructorLayout: React.FC = () => {
    const { user, logout } = useAuth()
    const navigate = useNavigate()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { mode, toggleMode } = useTheme()

    // Lock body scroll when mobile sidebar is open
    useEffect(() => {
        if (sidebarOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [sidebarOpen])

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-main dark:text-white font-body">
            <Sidebar
                sections={instructorSections}
                title="Pelatihan Merdeka"
                accentColor="primary"
                logo="/logo.svg"
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />

            {/* Main Content */}
            <main className="md:ml-64 min-h-screen">
                {/* Top Bar */}
                <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-8 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-md shadow-sm dark:shadow-none dark:border-b dark:border-white/5">
                    <div className="flex items-center gap-3 md:gap-4">
                        {/* Mobile hamburger menu */}
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="md:hidden size-10 rounded-lg bg-slate-100 dark:bg-surface-card flex items-center justify-center hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px]">menu</span>
                        </button>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={toggleMode}
                            className="size-10 rounded-lg bg-slate-100 dark:bg-surface-card flex items-center justify-center hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                            aria-label={`switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
                        >
                            {mode === 'light' ? (
                                <MoonIcon className="h-5 w-5" />
                            ) : (
                                <SunIcon className="h-5 w-5" />
                            )}
                        </button>
                        <NotificationDropdown userId={user?.id} role={user?.role || 'instructor'} />

                        {/* Profile Dropdown */}
                        <Menu as="div" className="relative">
                            <Menu.Button className="flex items-center gap-3 pl-4 border-l border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 rounded-lg py-2 pr-3 transition-colors cursor-pointer">
                                <Avatar
                                    name={user?.name || 'Guest'}
                                    size="sm"
                                    className="ring-2 ring-primary/30"
                                />
                                <div className="hidden md:block text-left">
                                    <p className="text-sm font-bold text-slate-900 dark:text-white">{user?.name || 'Instructor'}</p>
                                    <p className="text-xs text-slate-500 dark:text-text-muted">Guru</p>
                                </div>
                                <span className="material-symbols-outlined text-[18px] text-slate-400">expand_more</span>
                            </Menu.Button>

                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg bg-white dark:bg-surface-card shadow-lg ring-1 ring-gray-100 dark:ring-white/10 focus:outline-none overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-100 dark:border-white/10">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{user?.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-text-muted truncate">{user?.email}</p>
                                    </div>
                                    <div className="py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/instructor/profile"
                                                    className={`${active ? 'bg-gray-100 dark:bg-white/5' : ''} flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">person</span>
                                                    Profile
                                                </Link>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/"
                                                    className={`${active ? 'bg-gray-100 dark:bg-white/5' : ''} flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">home</span>
                                                    Back to Home
                                                </Link>
                                            )}
                                        </Menu.Item>
                                    </div>
                                    <div className="border-t border-gray-100 dark:border-white/10 py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    onClick={handleLogout}
                                                    className={`${active ? 'bg-red-50 dark:bg-red-500/10' : ''} flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 dark:text-red-400 w-full text-left`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">logout</span>
                                                    Logout
                                                </button>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                </header>

                {/* Page Content */}
                <div className="p-4 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    )
}

export default InstructorLayout
